import * as React from 'react'
import { createPortal } from 'react-dom'
import * as API from '../lib/api'

// Trial Explainer info box (CD-172): an anchored desktop popover / centered
// mobile modal showing the trial's per-product-type download breakdown.
// Hand-rolled because React 16 (ADR-CD-039) has no floating-ui/popper, and
// `react-modal` can't produce the anchored desktop look.

// Foundation's `small only` upper bound: strictly below this we render the
// centered mobile presentation, so 640px matches Foundation's breakpoint.
const MOBILE_MAX_WIDTH = 640
// Past this many items the desktop card balances into two columns; more items
// grow rows, never a third column.
const COLUMN_THRESHOLD = 8

// Close grace outlasts the pointer's trip across the 12px anchor gap into the box.
const HOVER_OPEN_DELAY = 120
const HOVER_CLOSE_DELAY = 150

// React 16 has no `useId`; hand out monotonically increasing per-instance ids
// for `aria-labelledby` (allocated once per mount via the lazy initializer below).
let explainerInstanceCount = 0

// Tabbable elements inside the dialog, used to bound the focus trap.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

interface ITrialExplainerProps {
  planName: string
  // Already ordered by `SiteProductType#display_order` server-side.
  items: API.ITrialDownloadItem[]
  children: React.ReactNode
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_MAX_WIDTH
}

function isHoverCapable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover)').matches
  )
}

function downloadLabel(count: number): string {
  return `${count} ${count === 1 ? 'download' : 'downloads'}`
}

export function TrialExplainer({ planName, items, children }: ITrialExplainerProps) {
  const [open, setOpen] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(isMobileViewport)
  const [hoverCapable, setHoverCapable] = React.useState(isHoverCapable)
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const boxRef = React.useRef<HTMLDivElement>(null)
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  const openTimerRef = React.useRef<number | null>(null)
  const closeTimerRef = React.useRef<number | null>(null)
  // 'hover' opens follow the pointer: no focus steal, auto-close on mouse-leave.
  // 'manual' (click/keyboard) opens move focus in; only ESC/outside-click close.
  const openSourceRef = React.useRef<'hover' | 'manual'>('manual')
  const [titleId] = React.useState(() => `trial-explainer-title-${(explainerInstanceCount += 1)}`)

  function close() {
    setOpen(false)
    if (triggerRef.current) { triggerRef.current.focus() }
  }

  function clearOpenTimer() {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }

  function clearCloseTimer() {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  function scheduleClose() {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null
      setOpen(false)
    }, HOVER_CLOSE_DELAY)
  }

  const hoverEnabled = hoverCapable && !isMobile

  function handleTriggerMouseEnter() {
    if (!hoverEnabled) { return }
    clearCloseTimer()
    clearOpenTimer()
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null
      // Set the source before setOpen so the focus effect sees 'hover'.
      openSourceRef.current = 'hover'
      setOpen(true)
    }, HOVER_OPEN_DELAY)
  }

  function handleTriggerMouseLeave() {
    if (!hoverEnabled) { return }
    clearOpenTimer()
    if (openSourceRef.current === 'hover') { scheduleClose() }
  }

  function handleBoxMouseEnter() {
    if (!hoverEnabled) { return }
    clearCloseTimer()
  }

  function handleBoxMouseLeave() {
    if (!hoverEnabled) { return }
    if (openSourceRef.current === 'hover') { scheduleClose() }
  }

  // Track viewport size so the anchored-vs-centered presentation follows resize.
  React.useEffect(() => {
    function onResize() { setIsMobile(isMobileViewport()) }

    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }

    const query = window.matchMedia('(hover: hover)')
    function onChange() { setHoverCapable(query.matches) }

    // IE11's MediaQueryList predates addEventListener; fall back to addListener.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange)
      return () => query.removeEventListener('change', onChange)
    }
    query.addListener(onChange)
    return () => query.removeListener(onChange)
  }, [])

  React.useEffect(() => () => {
    clearOpenTimer()
    clearCloseTimer()
  }, [])

  // Reset to 'manual' on close so a stale 'hover' can't misroute the next open.
  React.useEffect(() => {
    if (!open) { openSourceRef.current = 'manual' }
  }, [open])

  // ESC and outside-click/tap dismissal, only wired while open.
  React.useEffect(() => {
    if (!open) { return undefined }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') { close() }
    }

    function onPointerDown(event: Event) {
      const target = event.target as Node
      if (boxRef.current && boxRef.current.contains(target)) { return }
      if (triggerRef.current && triggerRef.current.contains(target)) { return }
      setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [open])

  // Focus trap + scroll lock apply only to the mobile modal. Desktop is a non-modal
  // popover (WAI-ARIA): Tab moves out naturally. A hover open must not steal focus.
  React.useEffect(() => {
    if (!open) { return undefined }
    const box = boxRef.current
    if (!box) { return undefined }

    const isHoverOpen = openSourceRef.current === 'hover' && !isMobile
    if (!isHoverOpen) {
      const focusTarget = (isMobile && closeButtonRef.current) || box
      focusTarget.focus()
    }

    if (!isMobile) { return undefined }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') { return }

      const focusable = Array.from(box!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first) { return }
      const active = document.activeElement

      if (event.shiftKey) {
        if (active === first || !box!.contains(active)) {
          event.preventDefault()
          last.focus()
        }
      } else if (active === last || !box!.contains(active)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, isMobile])

  // Anchor the desktop card to the trigger, clamped into the viewport and
  // flipped above if it would overflow the bottom. Mobile centers via CSS.
  React.useLayoutEffect(() => {
    if (!open || isMobile) { return undefined }

    function reposition() {
      const trigger = triggerRef.current
      if (!trigger) { return }

      const rect = trigger.getBoundingClientRect()
      const gap = 12
      let top = rect.bottom + gap
      let left = rect.left

      const box = boxRef.current
      if (box) {
        const maxLeft = window.innerWidth - box.offsetWidth - gap
        left = Math.max(gap, Math.min(left, maxLeft))
        const maxTop = window.innerHeight - box.offsetHeight - gap
        if (top > maxTop) { top = Math.max(gap, rect.top - box.offsetHeight - gap) }
      }

      setCoords({ top, left })
    }

    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, isMobile, items.length])

  function renderPopover() {
    const columns = items.length > COLUMN_THRESHOLD
    const boxStyle: React.CSSProperties =
      isMobile || !coords ? {} : { position: 'fixed', top: coords.top, left: coords.left }

    const popover = (
      <div className={`trial-explainer trial-explainer--${isMobile ? 'mobile' : 'desktop'}`}>
        {isMobile ? <div className="trial-explainer__backdrop" /> : null}
        <div
          ref={boxRef}
          className="trial-explainer__box"
          role="dialog"
          aria-labelledby={titleId}
          aria-modal={isMobile ? true : undefined}
          tabIndex={-1}
          style={boxStyle}
          onMouseEnter={handleBoxMouseEnter}
          onMouseLeave={handleBoxMouseLeave}
        >
          {isMobile ? (
            <button
              ref={closeButtonRef}
              type="button"
              className="trial-explainer__close"
              aria-label="Close"
              onClick={close}
            >
              &times;
            </button>
          ) : null}
          <p id={titleId} className="trial-explainer__title">
            Your <strong>{planName}</strong> trial includes:
          </p>
          <hr className="trial-explainer__divider" />
          <ol
            className={`trial-explainer__list${columns ? ' trial-explainer__list--columns' : ''}`}
          >
            {items.map((item) => (
              <li key={item.name} className="trial-explainer__item">
                <span className="trial-explainer__item-name">{item.name}</span>{' '}
                <span className="trial-explainer__item-count">({downloadLabel(item.count)})</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    )

    return createPortal(popover, document.body)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="trial-explainer__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          clearOpenTimer()
          clearCloseTimer()
          openSourceRef.current = 'manual'
          setOpen((isOpen) => !isOpen)
        }}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
      >
        {children}
      </button>
      {open ? renderPopover() : null}
    </>
  )
}
